import React, { useState, useEffect, useRef } from 'react';
import { parse } from 'date-fns';
import { checkValidDate } from '@common/utils/index';
import SatQuestionArea from '@components/common/satUI/SatQuestionArea';
import SatInput from '@components/common/satUI/SatInput';
import SatButton from '@components/common/satUI/SatButton';
import SatSelect from '@components/common/satUI/SatSelect';
import SatValidationErrMsg from '@components/common/satUI/SatValidationErrMsg';

const EtcInfo = props => {
  const {
    category1,
    category4,
    onChange,
    showModal,
    onNext,
    startdate: notificationInfoStartdate,
    enddate: notificationInfoEnddate,
    isEasyProduct,
    isMedicalProduct,
    suitabilityInfo,
    suitabilityIncome,
    isStbltDgnsProcExcpt,
    isProFinance,
    isPassCustomerMinor,
    isBeforeRevision,
  } = props;
  const inputStartRef = useRef();
  const inputEndRef = useRef();
  const workplaceInputRef = useRef();
  const workareaInputRef = useRef();
  const industryInputRef = useRef();
  const specifictaskInputRef = useRef();
  const areaRef = useRef();
  const purposeRef = useRef();
  const heightInputRef = useRef();
  const weightInputRef = useRef();

  const startdate = $utils.removeDotDate(notificationInfoStartdate);
  const enddate = $utils.removeDotDate(notificationInfoEnddate);
  const travelDateLength = 6; // 여행질문 날짜 형식 (8: yyyy.MM.dd / 6: yy.MM.dd)
  const travelDateFormat = travelDateLength === 6 ? 'yyMMdd' : 'yyyyMMdd';

  const [isDisabled, setIsDisabled] = useState(true);
  const [showErrorCheckMsg, setShowErrorCheckMsg] = useState(false);
  const [isDateError, setIsDateError] = useState(false);
  const [purposeText, setPurposeText] = useState(
    ['1', '2', '3', '4', '5'].includes(category4?.q04?.purpose)
      ? ''
      : category4?.q04?.purpose,
  );

  // 월소득 select Data
  const incomeSelectData = [
    { label: '소득없음', value: 1 },
    { label: '200만원 미만', value: 2 },
    { label: '500만원 미만', value: 3 },
    { label: '1000만원 미만', value: 4 },
    { label: '1000만원 이상', value: 5 },
  ];
  // 음주량 select Data
  const drinkingamountSelectData = [
    { label: '0.5병', value: '0.5' },
    { label: '1병', value: '1' },
    { label: '1.5병', value: '1.5' },
    { label: '2병', value: '2' },
    { label: '3병이상', value: '3▲' },
  ];
  // 흡연량 select Data
  const smokingcountSelectData = [
    { label: '20개비 이하', value: '20▼' },
    { label: '21~30개비', value: '21~30' },
    { label: '31~40개비', value: '31~40' },
    { label: '40개비 이상', value: '40▲' },
  ];
  // 흡연기간 select Data
  const smokingperiodSelectData = [
    { label: '1~5년', value: '1~5' },
    { label: '6~10년', value: '6~10' },
    { label: '11~20년', value: '11~20' },
    { label: '20년이상', value: '20▲' },
  ];
  // 여행목적 select Data
  const purposeSelectData = [
    { label: '여행목적 선택', value: '1' },
    { label: '출장', value: '2' },
    { label: '여행', value: '3' },
    { label: '친지방문', value: '4' },
    { label: '기타 (직접입력)', value: '5' },
  ];

  useEffect(() => {
    checkButtonVisible(isEasyProduct ? category1 : category4);
  }, [category1, category4, purposeText]);

  useEffect(() => {
    checkButtonVisible(isEasyProduct ? category1 : category4);
  }, [isEasyProduct]);

  // 이전에 기록된 startdate, enddate 형식이 달라서 최초 로딩 시 날짜 형식 확인하여 맞춤
  // 참고: 이전 형식 (yyyyMMdd) / 변경된 형식 (yy.MM.dd)
  useEffect(() => {
    if (category4?.q04?.checkYn === 'Y') {
      const startdateValue = startdate?.slice(-travelDateLength);
      const enddateValue = enddate?.slice(-travelDateLength);

      const changeData = {
        ...category4,
        q04: {
          ...category4?.q04,
          startdate: $utils.addDotDate(startdateValue ?? startdate),
          enddate: $utils.addDotDate(enddateValue ?? enddate),
        },
      };

      onChange({ category4: changeData });
    }
  }, []);

  // 날짜 변경 시, 유효성 체크
  useEffect(() => {
    const startDateString = $utils.convertDateString(
      startdate,
      'yyyy.MM.dd',
      travelDateFormat,
    );
    const enddateString = $utils.convertDateString(
      enddate,
      'yyyy.MM.dd',
      travelDateFormat,
    );

    if (
      (startdate !== '' && enddate !== '' && startdate > enddate) ||
      (startdate !== '' && !checkValidDate(startDateString)) ||
      (enddate !== '' && !checkValidDate(enddateString))
    ) {
      setIsDateError(true);
    } else {
      setIsDateError(false);
    }
  }, [startdate, enddate]);

  // input onChange
  const handleInputOnChange = (type, value) => {
    if (type === 'height') {
      handleSaveInputData('q08', 'height', value);
    } else if (type === 'weight') {
      handleSaveInputData('q08', 'weight', value);
    }
  };

  // save DATA
  const handleSaveInputData = (type, fieldName, value) => {
    let changeData;
    if (!isEasyProduct) {
      changeData = {
        ...category4,
        [type]: {
          ...category4[type],
          [fieldName]: value,
        },
      };
      onChange({
        category4: changeData,
      });
    } else {
      changeData = {
        ...category1,
        [type]: {
          ...category1[type],
          [fieldName]: value,
        },
      };
      onChange({
        category1: changeData,
      });
    }

    checkButtonVisible(changeData);
  };

  // 월평균 소득 선택
  const handleSelectIncome = value => {
    let changeData;
    if (!isEasyProduct) {
      changeData = {
        ...category4,
        q02: value.value,
      };
      onChange({
        category4: changeData,
      });
    } else {
      changeData = {
        ...category1,
        q07: value.value,
      };
      onChange({
        category1: changeData,
      });
    }

    checkButtonVisible(changeData);
  };

  // 음주 선택
  const handleChangeDrinking = (type, value) => {
    const changeData = {
      ...category4,
      q05: {
        ...category4?.q05,
        [type]: value,
      },
    };
    onChange({
      category4: changeData,
    });

    checkButtonVisible(changeData);
  };

  // 흡연 선택
  const handleSelectSmoking = (type, value) => {
    const changeData = {
      ...category4,
      q06: {
        ...category4?.q06,
        [type]: value.value,
      },
    };
    onChange({
      category4: changeData,
    });

    checkButtonVisible(changeData);
  };

  //여행 기간 선택
  const onSelectDate = (type, value) => {
    try {
      // 입력된 날짜의 정해진 길이 확인
      const isValidDateLength = value.length === travelDateLength;
      if (!isValidDateLength)
        throw new Error(`날짜를 ${travelDateLength}자리로 입력해주세요.`);

      // travelDateLength 관계 없이 yyyyMMdd 형태로 만든값
      const dateString = $utils.convertDateString(
        value,
        'yyyyMMdd',
        travelDateFormat,
      );

      // 입력된 날짜 유효성 체크
      const dateFormat = 'yyyyMMdd';
      const todayDate = new Date();
      const inputDate = parse(dateString, dateFormat, new Date());
      const isAfterToday = todayDate.getTime() <= inputDate.getTime();
      if (!isAfterToday)
        throw new Error('오늘 보다 이전 날짜를 입력하셨습니다.');

      const changeData = {
        ...category4,
        q04: {
          ...category4?.q04,
          [type]: $utils.addDotDate(value),
        },
      };

      onChange({
        category4: changeData,
      });

      checkButtonVisible(changeData);
    } catch (error) {
      console.log('onSelectDate() - error', error);

      setIsDateError(true);
    }
  };

  // 예, 아니오 버튼
  const handleButtonYnClick = (type, value) => {
    if (
      value === 'N' &&
      (type === 'q03' || type === 'q04' || type === 'q05' || type === 'q06')
    ) {
      // 아니오 버튼 선택 시 하위 요소 모두 해제
      for (const key of Object.keys(category4[type])) {
        category4[type][key] = '';
      }
    }

    if (type == 'q07' && value === 'Y') {
      showModal(
        '당사는 한국신용정보원을 통하여 귀하의 타사 가입정보를 확인할 예정입니다.',
      );
    }

    const changeData = {
      ...category4,
      [type]: {
        ...category4[type],
        checkYn: value,
      },
    };
    onChange({
      category4: changeData,
    });

    checkButtonVisible(changeData);
  };

  const checkButtonVisible = changeData => {
    const notificationIncome = parseInt(
      isEasyProduct ? category1.q07 : category4?.q02,
    );
    const isSameIncome = notificationIncome === suitabilityIncome;

    // 하단 다음 버튼 visible 처리
    let disabledYn = false;
    // 실손상품
    if (isMedicalProduct) {
      for (const fieldName of Object.keys(changeData['q01'])) {
        if (!isBeforeRevision && fieldName === 'workarea') {
          // 2025.01부터는 계약전알릴의무에서 근무지역 받지 않음.
        } else if (changeData['q01'][fieldName] === '') {
          disabledYn = true;
        }
      }
    } else if (!isEasyProduct) {
      outerfor: for (const key of Object.keys(changeData)) {
        if (changeData[key].checkYn === '') {
          // 예아니오 미선택항목 확인
          if (!isBeforeRevision && key === 'q07') {
            // 2025.01부터는 계약전알릴의무에서 다른 보험회사 항목은 받지 않음.
          } else {
            disabledYn = true;
            break outerfor;
          }
        } else {
          // 세부 항목 미입력 여부 확인
          if (key === 'q01' || key === 'q02' || key === 'q08') {
            for (const fieldName of Object.keys(changeData[key])) {
              if (!isBeforeRevision && fieldName === 'workarea') {
                // 2025.01부터는 계약전알릴의무에서 근무지역 받지 않음.
              } else if (
                changeData[key][fieldName] === '' ||
                (key === 'q08' &&
                  (changeData[key][fieldName] === '0' ||
                    changeData[key]['height'] >= '250')) // hanjy: 키 250 이상 입력할 경우 disabled
              ) {
                disabledYn = true;
                break outerfor;
              }

              if (notificationIncome === '') {
                disabledYn = true;
                break outerfor;
              }
              if (
                !isStbltDgnsProcExcpt &&
                !isProFinance &&
                !isPassCustomerMinor &&
                suitabilityIncome !== ''
              ) {
                if (
                  suitabilityInfo.variableProductYn === 'Y' ||
                  suitabilityInfo.usdProductYN === 'Y'
                ) {
                  if (!isSameIncome) {
                    //만약 200만원 미만이라면
                    if (suitabilityIncome == 2 && notificationIncome === 1) {
                      const work = !isEasyProduct
                        ? category4?.q01
                        : category1.q06;
                      if (
                        Object.values(work).filter(item => item === '')
                          .length === 0
                      )
                        break outerfor;
                    }
                    disabledYn = true;
                    break outerfor;
                  }
                  // else {
                  //   disabledYn = false;
                  // }
                }
              }
            }
          } else {
            // '예'로 체크 했을 때, 나머지 field의 Validation 체크하지 않을 문항들
            // Q7: 다른보험회사 상품 여부 '예' 한 경우, checkYn외 otherCompany1 등 field 가 채워졌는지 확인하지 않음
            const exceptionQuestions = ['q07'];
            const isException = exceptionQuestions.includes(key);

            if (
              isBeforeRevision &&
              !isException &&
              changeData[key].checkYn === 'Y'
            ) {
              for (const fieldName of Object.keys(changeData[key])) {
                if (fieldName !== 'checkYn') {
                  if (key === 'q04' && fieldName === 'purpose') {
                    if (
                      (!['1', '2', '3', '4'].includes(
                        changeData[key][fieldName],
                      ) &&
                        purposeText.length < 2) ||
                      changeData[key][fieldName] === '1' ||
                      changeData[key][fieldName] === ''
                    ) {
                      disabledYn = true;
                      break outerfor;
                    }
                  } else if (key === 'q04') {
                    if (
                      changeData.q04.checkYn === 'Y' &&
                      (startdate?.length < travelDateLength ||
                        enddate?.length < travelDateLength ||
                        (startdate !== '' &&
                          enddate !== '' &&
                          startdate > enddate) ||
                        (startdate !== '' &&
                          !checkValidDate(
                            $utils.convertDateString(
                              startdate,
                              'yyyy.MM.dd',
                              travelDateFormat,
                            ),
                          )) ||
                        (enddate !== '' &&
                          !checkValidDate(
                            $utils.convertDateString(
                              enddate,
                              'yyyy.MM.dd',
                              travelDateFormat,
                            ),
                          )))
                    ) {
                      disabledYn = true;
                      break outerfor;
                    }
                  } else {
                    if (changeData[key][fieldName] === '') {
                      disabledYn = true;
                      break outerfor;
                    }
                  }
                }
              }
            }
          }
        }
      }
    } else {
      // 간편상품
      if (changeData.q06.checkYn === '') {
        delete changeData.q06.checkYn;
      }
      if (changeData.q06.pregnancyPeriod === '') {
        delete changeData.q06.pregnancyPeriod;
      }
      if (Object.keys(changeData.q06).length < 4) {
        return setIsDisabled(true);
      }
      for (const fieldName of Object.keys(changeData.q06)) {
        if (!isBeforeRevision && fieldName === 'workarea') {
          // 2025.01부터는 계약전알릴의무에서 근무지역 받지 않음.
        } else if (changeData.q06[fieldName] === '') {
          disabledYn = true;
          break;
        }
      }

      if (changeData.q07 === '') {
        disabledYn = true;
      }
      if (
        !isStbltDgnsProcExcpt &&
        !isProFinance &&
        !isPassCustomerMinor &&
        suitabilityIncome !== ''
      ) {
        if (
          suitabilityInfo.variableProductYn === 'Y' ||
          suitabilityInfo.usdProductYN === 'Y'
        ) {
          if (
            suitabilityIncome !==
            (isEasyProduct ? category1.q07 : category4?.q02)
          ) {
            //만약 200만원 미만이라면
            if (
              suitabilityIncome == 2 &&
              (isEasyProduct ? category1.q07 : category4?.q02) === 1
            ) {
              const work = category1.q06;

              if (
                Object.keys(work).length === 4 &&
                Object.values(work).filter(item => item === '').length === 0
              )
                disabledYn = false;
              return setIsDisabled(disabledYn);
            }
            disabledYn = true;
          }
          // else {
          //   disabledYn = false;
          // }
        }
      }
    }
    setIsDisabled(disabledYn);
  };

  const handleOnClick = () => {
    // 하단 다음 버튼
    if (isDisabled) {
      setShowErrorCheckMsg(true);
      // 입력 안 된 가장 첫번째 부분에 포커스
      if (!workplaceInputRef.current.isValidCheck()) {
        workplaceInputRef.current.onFocus();
      } else if (isBeforeRevision && !workareaInputRef.current.isValidCheck()) {
        workareaInputRef.current.onFocus();
      } else if (!industryInputRef.current.isValidCheck()) {
        industryInputRef.current.onFocus();
      } else if (!specifictaskInputRef.current.isValidCheck()) {
        specifictaskInputRef.current.onFocus();
      }
      if (!isEasyProduct && !isMedicalProduct) {
        if (!heightInputRef.current.isValidCheck()) {
          heightInputRef.current.onFocus();
        } else if (!weightInputRef.current.isValidCheck()) {
          weightInputRef.current.onFocus();
        }

        if (category4?.q04?.checkYn === 'Y') {
          if (!areaRef.current.isValidCheck()) {
            areaRef.current.onFocus();
          } else if (
            purposeRef.current?.isValidCheck &&
            !purposeRef.current.isValidCheck()
          ) {
            purposeRef.current.onFocus();
          }
        }
      }
    } else {
      onNext();
    }
  };

  const incomeErrorMessage = () => {
    const beforeCode = incomeSelectData.find(
      item => item.value === suitabilityIncome - 1,
    ).value;
    const beforeLabel = incomeSelectData.find(
      item => item.value === suitabilityIncome - 1,
    ).label;
    const label = incomeSelectData.find(
      item => item.value === suitabilityIncome,
    ).label;
    const scope =
      beforeCode <= 1 || beforeCode === 4
        ? label
        : `${beforeLabel.replace('미만', '')}~${label}`;

    return `적합성 진단 월 소득은 ${scope}이며, 상이할 경우 청약 진행이 불가능합니다.`;
  };

  const isErrorMessageIncome = () => {
    const notificationIncome = parseInt(
      isEasyProduct ? category1.q07 : category4?.q02,
    );
    const isSameIncome = notificationIncome === suitabilityIncome;

    // 선택된 값이 없으면
    if (notificationIncome === '') return true;

    //선택한 적합성금액과 정보가 안맞으면
    if (!isSameIncome) {
      if (
        //특히 200만원 미만일때는 소득없음도 선택 될 수 있다.
        suitabilityIncome === 2 &&
        notificationIncome < 2
      ) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  };

  const getIncomeValue = () => {
    const income = isEasyProduct ? category1.q07 : category4?.q02;
    const incomeNumber = parseInt(income);
    const incomeValue = isNaN(incomeNumber) ? '' : incomeNumber;

    return incomeValue;
  };

  return (
    <React.Fragment>
      <p className="text-info2">
        ※ 보험계약 체결 당시 직업 또는 직무를 사실대로 알리지 않거나 보험계약
        체결 후 직업 또는 직무가 변경된 사실(예:사무관리 ← → 현장관리)을
        지체없이 회사에 알리지 않은 경우 계약 해지 등 알릴 의무 위반에 따른
        불이익이 발생할 수 있습니다.
      </p>
      <div className="infor-list">
        <div className="insu-box">
          <div className="txt-question">
            <p>1. 귀하의 직업은 무엇인가요?</p>
          </div>
        </div>
        <div className="box two a-fs">
          <div className="item-w50 block">
            <SatInput
              ref={workplaceInputRef}
              value={
                !isEasyProduct
                  ? category4?.q01.workplace
                  : category1.q06.workplace
              }
              type="text"
              label="근무처 (회사명)"
              maxLength={15}
              onChange={value => {
                handleSaveInputData(
                  isEasyProduct ? 'q06' : 'q01',
                  'workplace',
                  value,
                );
              }}
              validRule={[
                {
                  required: true,
                  errMsg: '근무처를 입력해 주세요.',
                },
              ]}
            />
          </div>
          <div className="item-w50 block">
            {isBeforeRevision ? (
              <SatInput
                ref={workareaInputRef}
                value={
                  !isEasyProduct
                    ? category4?.q01.workarea
                    : category1.q06.workarea
                }
                type="text"
                label="근무지역 (예 : 서울 강남구)"
                maxLength={10}
                onChange={value => {
                  handleSaveInputData(
                    isEasyProduct ? 'q06' : 'q01',
                    'workarea',
                    value,
                  );
                }}
                validRule={[
                  {
                    required: true,
                    errMsg: '근무지역을 입력해 주세요.',
                  },
                ]}
              />
            ) : (
              <SatInput
                ref={industryInputRef}
                value={
                  !isEasyProduct
                    ? category4?.q01.industry
                    : category1.q06.industry
                }
                type="text"
                label="업종 (예 : 서비스업, 금융업 등)"
                maxLength={10}
                onChange={value => {
                  handleSaveInputData(
                    isEasyProduct ? 'q06' : 'q01',
                    'industry',
                    value,
                  );
                }}
                validRule={[
                  {
                    required: true,
                    errMsg: '업종을 입력해 주세요.',
                  },
                ]}
              />
            )}
          </div>
        </div>
        {isBeforeRevision && (
          <div className="box two a-fs">
            <div className="item-w50 block">
              <SatInput
                ref={industryInputRef}
                value={
                  !isEasyProduct
                    ? category4?.q01.industry
                    : category1.q06.industry
                }
                type="text"
                label="업종 (예 : 서비스업, 금융업 등)"
                maxLength={10}
                onChange={value => {
                  handleSaveInputData(
                    isEasyProduct ? 'q06' : 'q01',
                    'industry',
                    value,
                  );
                }}
                validRule={[
                  {
                    required: true,
                    errMsg: '업종을 입력해 주세요.',
                  },
                ]}
              />
            </div>
          </div>
        )}
        <div className="box mgb-20 a-fs">
          <div className="w100 block">
            <SatInput
              ref={specifictaskInputRef}
              value={
                !isEasyProduct
                  ? category4?.q01.specifictask
                  : category1.q06.specifictask
              }
              type="text"
              label="구체적인 업무 (예 : 사무직, 판매직 등)"
              maxLength={40}
              onChange={value => {
                handleSaveInputData(
                  isEasyProduct ? 'q06' : 'q01',
                  'specifictask',
                  value,
                );
              }}
              validRule={[
                {
                  required: true,
                  errMsg: '구체적 업무를 입력해 주세요.',
                },
              ]}
            />
          </div>
        </div>
      </div>
      {!isMedicalProduct && (
        <>
          <div className="infor-list">
            <div className="insu-box">
              <div className="txt-question">
                <p>2. 월평균 소득은 어떻게 되시나요?</p>
                <SatValidationErrMsg
                  visible={
                    showErrorCheckMsg &&
                    ((!isEasyProduct && category4?.q02 === '') ||
                      (isEasyProduct && category1.q07 === ''))
                  }
                  type="error"
                  message="월소득을 입력해 주세요."
                />
              </div>
            </div>
            <div className="box">
              <div>
                <SatSelect
                  customStyleClass="w-200"
                  value={
                    getIncomeValue()

                    // isEasyProduct
                    //   ? typeof category1.q07 === 'string'
                    //     ? ''
                    //     : category1.q07
                    //   : typeof category4?.q02 === 'string'
                    //   ? ''
                    //   : category4?.q02,
                  }
                  title="선택"
                  initOption={{ label: '선택', value: '' }}
                  options={incomeSelectData}
                  onChange={handleSelectIncome}
                />
                {suitabilityIncome !== '' &&
                  !isStbltDgnsProcExcpt &&
                  !isProFinance &&
                  !isPassCustomerMinor &&
                  (suitabilityInfo.variableProductYn === 'Y' ||
                    suitabilityInfo.usdProductYN === 'Y') && (
                    <SatValidationErrMsg
                      visible={isErrorMessageIncome()}
                      type="error"
                      message={incomeErrorMessage()}
                      wide={true}
                    />
                  )}
              </div>
            </div>
          </div>
          {!isEasyProduct && (
            <>
              <div className="infor-list">
                <SatQuestionArea
                  title={() => (
                    <p>
                      3. 부업 또는 겸업, 계절적으로 종사하는 업무가 있으신가요?
                    </p>
                  )}
                  selectValue={category4?.q03.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category4_q3'}
                  onClick={value => handleButtonYnClick('q03', value)}
                  error={
                    showErrorCheckMsg && category4?.q03.checkYn === ''
                      ? '부업/겸업, 계절적 종사 업무 여부를 선택해 주세요.'
                      : showErrorCheckMsg &&
                        category4?.q03.checkYn === 'Y' &&
                        category4?.q03.specifictask === '' &&
                        '부업/겸업, 계절적 종사 업무를 입력해 주세요.'
                  }
                />
                {category4?.q03.checkYn === 'Y' && (
                  <div className="box">
                    <SatInput
                      value={category4?.q03.specifictask}
                      type="text"
                      label="구체적인 업무"
                      maxLength={40}
                      onChange={value => {
                        handleSaveInputData('q03', 'specifictask', value);
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="infor-list">
                <SatQuestionArea
                  title={() => (
                    <p>
                      4. 향후 3개월 이내에 다음과 같은 해외지역으로 출국할
                      예정이 있습니까? (전쟁지역, 미개척지(열대&middot;한대),
                      등반산악지대)
                    </p>
                  )}
                  selectValue={category4?.q04?.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category4_q4'}
                  onClick={value => handleButtonYnClick('q04', value)}
                  error={
                    showErrorCheckMsg &&
                    category4?.q04?.checkYn === '' &&
                    '3개월 이내 출국예정 여부를 선택해 주세요.'
                  }
                />

                {category4?.q04?.checkYn === 'Y' && (
                  <>
                    <div className="insu-box">
                      <div className="txt-question">
                        <p>기간을 입력해주세요.</p>
                      </div>
                    </div>

                    <div className="box w-430 flex-column a-fs">
                      <div className="item-date">
                        <SatInput
                          useDate
                          ref={inputStartRef}
                          value={startdate}
                          type="number"
                          focusLabel="날짜 (6자리)"
                          maxLength={6}
                          dateLength={travelDateLength}
                          onChange={value => onSelectDate('startdate', value)}
                        />
                        <span className="bold">~</span>
                        <SatInput
                          useDate
                          ref={inputEndRef}
                          value={enddate}
                          type="number"
                          focusLabel="날짜 (6자리)"
                          maxLength={6}
                          dateLength={travelDateLength}
                          onChange={value => onSelectDate('enddate', value)}
                        />
                      </div>
                      <SatValidationErrMsg
                        visible={
                          isDateError ||
                          (showErrorCheckMsg &&
                            (startdate === '' || enddate === ''))
                        }
                        type="error"
                        message={
                          isDateError
                            ? '기간을 확인 후 입력해 주세요.'
                            : showErrorCheckMsg &&
                              (startdate === '' || enddate === '') &&
                              '기간을 입력해 주세요.'
                        }
                      />
                    </div>

                    <div className="insu-box">
                      <div className="txt-question">
                        <p>지역을 입력해주세요.</p>
                      </div>
                    </div>
                    <div className="box item-r flex-column a-fs">
                      <SatInput
                        ref={areaRef}
                        value={category4?.q04?.area}
                        type="text"
                        placeholder="입력"
                        maxLength={10}
                        onChange={value => {
                          handleSaveInputData('q04', 'area', value);
                        }}
                        validRule={[
                          {
                            required: true,
                            errMsg: '지역을 입력해 주세요.',
                          },
                        ]}
                      />
                    </div>

                    <div className="insu-box">
                      <div className="txt-question">
                        <p>여행목적을 선택해주세요.</p>
                      </div>
                    </div>
                    <div className="box item-r flex-column a-fs">
                      <div className="flex j-fs w100">
                        <SatSelect
                          customStyleClass="w-200"
                          value={
                            category4?.q04?.purpose === ''
                              ? '1'
                              : !['1', '2', '3', '4'].includes(
                                  category4?.q04?.purpose,
                                )
                              ? '5'
                              : category4?.q04?.purpose
                          }
                          title="선택"
                          options={purposeSelectData}
                          onChange={value => {
                            handleSaveInputData('q04', 'purpose', value.value);
                          }}
                        />
                        {!['', '1', '2', '3', '4'].includes(
                          category4?.q04?.purpose,
                        ) && (
                          <div className="mgl-24 flex1">
                            <SatInput
                              ref={purposeRef}
                              value={purposeText}
                              type="text"
                              placeholder="입력"
                              maxLength={10}
                              onChange={value => {
                                setPurposeText(value);
                              }}
                              onBlur={() => {
                                // hanjy: 여행목적 입력값 반영(입력 텍스트가 select 값이면 건너뜀)
                                if (
                                  !['1', '2', '3', '4', '5'].includes(
                                    purposeText,
                                  )
                                ) {
                                  handleSaveInputData(
                                    'q04',
                                    'purpose',
                                    purposeText,
                                  );
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <SatValidationErrMsg
                        visible={
                          showErrorCheckMsg &&
                          (category4?.q04?.purpose === '' ||
                            category4?.q04?.purpose === '1' ||
                            (!['1', '2', '3', '4'].includes(
                              category4?.q04?.purpose,
                            ) &&
                              purposeText.length < 2))
                        }
                        type="error"
                        message={`목적을 입력해 주세요${
                          !['', '1', '2', '3', '4'].includes(
                            category4?.q04?.purpose,
                          )
                            ? '(2자 이상)'
                            : ''
                        }.`}
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="infor-list">
                <SatQuestionArea
                  title={() => <p>5. 현재 음주를 하시나요?</p>}
                  selectValue={category4?.q05.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category4_q5'}
                  onClick={value => handleButtonYnClick('q05', value)}
                  error={
                    showErrorCheckMsg &&
                    category4?.q05.checkYn === '' &&
                    '음주여부를 선택해 주세요.'
                  }
                />
                {category4?.q05.checkYn === 'Y' && (
                  <>
                    <div className="insu-box">
                      <div className="txt-question">
                        <p>음주횟수 (1주기준)을 선택해주세요.</p>
                        <SatValidationErrMsg
                          visible={
                            showErrorCheckMsg &&
                            category4?.q05.drinkingcount === ''
                          }
                          type="error"
                          message="음주횟수를 입력해 주세요."
                        />
                      </div>
                    </div>
                    <div className="box">
                      <SatInput
                        type="number"
                        title="음주 빈도"
                        placeholder="음주 빈도 입력"
                        customStyleClass="w-200"
                        outerSuffix={() => <label className="txt">회</label>}
                        value={category4?.q05.drinkingcount}
                        onChange={value =>
                          handleChangeDrinking('drinkingcount', value)
                        }
                      />
                    </div>
                    <div className="insu-box">
                      <div className="txt-question">
                        <p>음주량 (소주기준 1회)을 선택해주세요.</p>
                        <SatValidationErrMsg
                          visible={
                            showErrorCheckMsg &&
                            category4?.q05.drinkingamount === ''
                          }
                          type="error"
                          message="음주량을 입력해 주세요."
                        />
                      </div>
                    </div>
                    <div className="box two">
                      <SatSelect
                        customStyleClass="w-200"
                        value={category4?.q05.drinkingamount}
                        title="병 선택"
                        initOption={{ label: '선택', value: '' }}
                        options={drinkingamountSelectData}
                        onChange={value =>
                          handleChangeDrinking('drinkingamount', value?.value)
                        }
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="infor-list">
                <SatQuestionArea
                  title={() => <p>6. 현재 흡연을 하시나요?</p>}
                  selectValue={category4?.q06.checkYn}
                  options={[
                    { label: '예', value: 'Y' },
                    { label: '아니요', value: 'N' },
                  ]}
                  useType={'btn'}
                  name={'category4_q6'}
                  onClick={value => handleButtonYnClick('q06', value)}
                  error={
                    showErrorCheckMsg &&
                    category4?.q06.checkYn === '' &&
                    '흡연여부를 선택해 주세요.'
                  }
                />
                {category4?.q06.checkYn === 'Y' && (
                  <>
                    <div className="insu-box">
                      <div className="txt-question">
                        <p>흡연량(1일 기준)을 선택해주세요.</p>
                        <SatValidationErrMsg
                          visible={
                            showErrorCheckMsg &&
                            category4?.q06.smokingcount === ''
                          }
                          type="error"
                          message="흡연량을 입력해 주세요."
                        />
                      </div>
                    </div>
                    <div className="box two">
                      <SatSelect
                        customStyleClass="w-200"
                        value={category4?.q06.smokingcount}
                        title="개비 선택"
                        initOption={{ label: '개비 선택', value: '' }}
                        options={smokingcountSelectData}
                        onChange={value =>
                          handleSelectSmoking('smokingcount', value)
                        }
                      />
                    </div>
                    <div className="insu-box">
                      <div className="txt-question">
                        <p>흡연기간 (현재부터 과거)을 선택해주세요.</p>
                        <SatValidationErrMsg
                          visible={
                            showErrorCheckMsg &&
                            category4?.q06.smokingperiod === ''
                          }
                          type="error"
                          message="흡연기간을 입력해 주세요."
                        />
                      </div>
                    </div>
                    <div className="box two">
                      <SatSelect
                        customStyleClass="w-200"
                        value={category4?.q06.smokingperiod}
                        title="년수 선택"
                        initOption={{ label: '년수 선택', value: '' }}
                        options={smokingperiodSelectData}
                        onChange={value =>
                          handleSelectSmoking('smokingperiod', value)
                        }
                      />
                    </div>
                  </>
                )}
              </div>
              {isBeforeRevision && (
                <div className="infor-list">
                  <SatQuestionArea
                    title={() => (
                      <p>
                        7. 다른 보험회사(우체국보험 및 각종 공제계약 판매사
                        포함)에 생명보험, 손해보험, 제3보험 또는 각종 공제계약을
                        가입하고 있습니까?
                      </p>
                    )}
                    selectValue={category4?.q07.checkYn}
                    options={[
                      { label: '예', value: 'Y' },
                      { label: '아니요', value: 'N' },
                    ]}
                    useType={'btn'}
                    name={'category4_q7'}
                    onClick={value => handleButtonYnClick('q07', value)}
                    error={
                      showErrorCheckMsg &&
                      category4?.q07.checkYn === '' &&
                      '다른 보험회사 가입여부를 선택해 주세요.'
                    }
                  />
                </div>
              )}
              <div className="infor-list">
                <div className="insu-box">
                  <div className="txt-question">
                    <p>{isBeforeRevision ? '8' : '7'}. 키와 몸무게를 입력해주세요.</p>
                  </div>
                </div>
                <div className="check-box inp-number">
                  <div className="item-w50">
                    <div className="amount">
                      <SatInput
                        ref={heightInputRef}
                        value={category4?.q08.height}
                        type="number"
                        placeholder="키"
                        maxLength={3}
                        onChange={value => handleInputOnChange('height', value)}
                        validRule={[
                          {
                            rule: v => v.length > 1,
                            errMsg: '키는 한자리 이상 입력해주세요.',
                          },
                          {
                            rule: v => v < 250,
                            errMsg: '키는 250CM 이상 입력할 수 없습니다.',
                          },
                          {
                            required: true,
                            errMsg: '키를 입력해 주세요.',
                          },
                        ]}
                        outerSuffix={() => <span className="txt">CM</span>}
                      />
                    </div>
                  </div>
                  <div className="item-w50">
                    <div className="amount">
                      <SatInput
                        ref={weightInputRef}
                        value={category4?.q08.weight}
                        type="number"
                        placeholder="몸무게"
                        maxLength={3}
                        onChange={value => handleInputOnChange('weight', value)}
                        outerSuffix={() => <span className="txt">kg</span>}
                        validRule={[
                          {
                            rule: v => v.length > 1,
                            errMsg: '몸무게를 한자리 이상 입력해주세요.',
                          },
                          {
                            required: true,
                            errMsg: '몸무게를 입력해 주세요.',
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <div className="infor-list">
        <div className="btn-area">
          <div className="box-btns-center">
            <SatButton
              styleClass={`btn-s blue btn-wide ${isDisabled ? 'disabled' : ''}`}
              label="다음"
              onClick={handleOnClick}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default EtcInfo;
